import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Database, AlertCircle, X, Clock, FileQuestion } from 'lucide-react';

interface Star {
  cx: number;
  cy: number;
  r: number;
  opacity: number;
  delay: number;
}

const VisualComparisonLeft: React.FC = React.memo(() => {
  // Memoize stars generation for performance
  const stars: Star[] = useMemo(() => {
    const starsArray = [];
    for (let i = 0; i < 100; i++) {
      starsArray.push({
        cx: Math.floor(Math.random() * 800),
        cy: Math.floor(Math.random() * 400),
        r: Math.random() * 1.5,
        opacity: Math.random() * 0.8 + 0.2,
        delay: (i % 5) * 0.2
      });
    }
    return starsArray;
  }, []);

  return (
    <section 
      className="relative bg-white p-8 rounded-xl shadow-sm border border-gray-100 w-full max-w-none"
      aria-labelledby="current-process-title"
      role="region"
    >
      {/* Title Section */}
      <header className="text-center mb-10">
        <h2 
          id="current-process-title" 
          className="text-2xl font-bold text-gray-800 mb-4"
        >
          Current Process
        </h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Sales and product teams manually collect customer needs, passing them into disconnected 
          systems where they remain siloed, manually triaged, and often forgotten.
        </p>
      </header>
      
      {/* Visual Section */}
      <div 
        className="relative mb-10"
        aria-label="Visual representation of disconnected systems"
        role="img"
      >
        <div className="w-full rounded-lg overflow-hidden relative bg-gray-900 h-[350px]">
          {/* CSS for animations */}
          <style>
            {`
              @keyframes rotate {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              
              @keyframes pulse {
                0% { opacity: 0.6; }
                50% { opacity: 0.8; }
                100% { opacity: 0.6; }
              }
              
              @keyframes twinkle {
                0% { opacity: 0.2; }
                50% { opacity: 0.8; }
                100% { opacity: 0.2; }
              }
              
              @keyframes drift {
                0% { transform: translateX(0px) translateY(0px); }
                50% { transform: translateX(-5px) translateY(5px); }
                100% { transform: translateX(0px) translateY(0px); }
              }
              
              @keyframes float {
                0% { transform: translate(0, 0) rotate(0deg); }
                33% { transform: translate(8px, -8px) rotate(3deg); }
                66% { transform: translate(-5px, 5px) rotate(-2deg); }
                100% { transform: translate(0, 0) rotate(0deg); }
              }
              
              .black-hole-outer {
                animation: pulse 4s infinite ease-in-out;
              }
              
              .black-hole-inner {
                animation: pulse 3s infinite ease-in-out;
              }
              
              .star-twinkle-0 { animation: twinkle 4s infinite ease-in-out; }
              .star-twinkle-1 { animation: twinkle 4s infinite ease-in-out; animation-delay: 0.2s; }
              .star-twinkle-2 { animation: twinkle 4s infinite ease-in-out; animation-delay: 0.4s; }
              .star-twinkle-3 { animation: twinkle 4s infinite ease-in-out; animation-delay: 0.6s; }
              .star-twinkle-4 { animation: twinkle 4s infinite ease-in-out; animation-delay: 0.8s; }
              
              .data-fragment-1 { animation: drift 10s infinite ease-in-out; animation-delay: 0.2s; }
              .data-fragment-2 { animation: drift 10s infinite ease-in-out; animation-delay: 0.4s; }
              .data-fragment-3 { animation: drift 10s infinite ease-in-out; animation-delay: 0.6s; }
              .data-fragment-4 { animation: drift 10s infinite ease-in-out; animation-delay: 0.8s; }
              
              .swirl-path-1 { animation: pulse 5s infinite ease-in-out; animation-delay: 0.1s; }
              .swirl-path-2 { animation: pulse 5s infinite ease-in-out; animation-delay: 0.3s; }
              .swirl-path-3 { animation: pulse 5s infinite ease-in-out; animation-delay: 0.5s; }
              .swirl-path-4 { animation: pulse 5s infinite ease-in-out; animation-delay: 0.7s; }
              
              .system-label { 
                font-size: 24px; 
                @media (min-width: 768px) {
                  font-size: 32px;
                }
                font-weight: 700; 
                fill: white; 
                filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.7));
                opacity: 0.9;
              }
              
              .float-1 { animation: float 20s infinite ease-in-out; }
              .float-2 { animation: float 15s infinite ease-in-out; animation-delay: 2s; }
              .float-3 { animation: float 18s infinite ease-in-out; animation-delay: 1s; }
              .float-4 { animation: float 25s infinite ease-in-out; animation-delay: 3s; }
              .float-5 { animation: float 22s infinite ease-in-out; animation-delay: 0.5s; }
            `}
          </style>
          
          {/* Black Hole SVG Illustration */}
          <svg 
            viewBox="0 0 800 400" 
            className="absolute inset-0 w-full h-full max-h-[400px] md:max-h-none"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Data black hole visualization"
          >
            {/* Background Stars */}
            <g role="presentation">
              {stars.map((star, i) => (
                <circle 
                  key={i}
                  cx={star.cx}
                  cy={star.cy}
                  r={star.r}
                  fill="#FFFFFF"
                  opacity={star.opacity}
                  className={`star-twinkle-${i % 5}`}
                />
              ))}
            </g>
            
            {/* Black Hole Elements */}
            <g role="presentation">
              <circle cx="400" cy="200" r="140" fill="url(#blackHoleGradient1)" opacity="0.6" className="black-hole-outer" />
              <circle cx="400" cy="200" r="100" fill="url(#blackHoleGradient2)" opacity="0.8" className="black-hole-inner" />
              <circle cx="400" cy="200" r="60" fill="#000000" />
            </g>
            
            {/* Data Icons Being Pulled In */}
            <g id="data-fragments" role="presentation">
              {/* Document Icon */}
              <g className="data-fragment-1">
                <path 
                  d="M200 120 L220 120 L220 150 L200 150 Z" 
                  fill="#E5E7EB" 
                  transform="rotate(15, 210, 135)"
                />
                <path 
                  d="M195 160 L225 160 L225 165 L195 165 Z" 
                  fill="#D1D5DB" 
                  transform="rotate(15, 210, 162.5)"
                />
                <path 
                  d="M195 170 L215 170 L215 175 L195 175 Z" 
                  fill="#D1D5DB" 
                  transform="rotate(15, 205, 172.5)"
                />
              </g>
              
              {/* Email Icon */}
              <g className="data-fragment-2">
                <rect 
                  x="290" 
                  y="290" 
                  width="30" 
                  height="20" 
                  rx="2" 
                  fill="#E5E7EB" 
                  transform="rotate(-20, 305, 300)"
                />
                <path 
                  d="M290 290 L305 300 L320 290" 
                  fill="none" 
                  stroke="#9CA3AF" 
                  strokeWidth="1" 
                  transform="rotate(-20, 305, 300)"
                />
              </g>
              
              {/* Data Chart */}
              <g className="data-fragment-3">
                <rect 
                  x="500" 
                  y="130" 
                  width="40" 
                  height="30" 
                  rx="2" 
                  fill="#E5E7EB" 
                  transform="rotate(-10, 520, 145)"
                />
                <line 
                  x1="505" 
                  y1="140" 
                  x2="535" 
                  y2="140" 
                  stroke="#9CA3AF" 
                  strokeWidth="1" 
                  transform="rotate(-10, 520, 145)"
                />
                <line 
                  x1="505" 
                  y1="150" 
                  x2="525" 
                  y2="150" 
                  stroke="#9CA3AF" 
                  strokeWidth="1" 
                  transform="rotate(-10, 520, 145)"
                />
              </g>
              
              {/* Message Icon */}
              <g className="data-fragment-4">
                <path 
                  d="M550 280 L590 280 L590 320 L570 310 L550 320 Z" 
                  fill="#E5E7EB" 
                  transform="rotate(15, 570, 300)"
                />
                <line 
                  x1="560" 
                  y1="290" 
                  x2="580" 
                  y2="290" 
                  stroke="#9CA3AF" 
                  strokeWidth="1" 
                  transform="rotate(15, 570, 300)"
                />
                <line 
                  x1="560" 
                  y1="300" 
                  x2="575" 
                  y2="300" 
                  stroke="#9CA3AF" 
                  strokeWidth="1" 
                  transform="rotate(15, 570, 300)"
                />
              </g>
              
              {/* Swirling Path 1 */}
              <path 
                d="M220 160 Q250 180, 300 210 T380 200" 
                fill="none" 
                stroke="#6B7280" 
                strokeWidth="1.5" 
                strokeDasharray="4,3"
                opacity="0.7"
                className="swirl-path-1"
              />
              
              {/* Swirling Path 2 */}
              <path 
                d="M520 150 Q500 170, 450 190 T390 200" 
                fill="none" 
                stroke="#6B7280" 
                strokeWidth="1.5" 
                strokeDasharray="4,3"
                opacity="0.7"
                className="swirl-path-2"
              />
              
              {/* Swirling Path 3 */}
              <path 
                d="M305 290 Q340 270, 370 240 T400 210" 
                fill="none" 
                stroke="#6B7280" 
                strokeWidth="1.5" 
                strokeDasharray="4,3"
                opacity="0.7"
                className="swirl-path-3"
              />
              
              {/* Swirling Path 4 */}
              <path 
                d="M570 300 Q540 280, 490 240 T420 210" 
                fill="none" 
                stroke="#6B7280" 
                strokeWidth="1.5" 
                strokeDasharray="4,3"
                opacity="0.7"
                className="swirl-path-4"
              />
            </g>
            
            {/* System Labels */}
            <g id="system-labels" role="presentation">
              {/* CRM Label */}
              <g className="float-1">
                <text x="160" y="70" textAnchor="middle" className="system-label" transform="rotate(-8, 160, 70)">CRM</text>
              </g>
              
              {/* Email Label */}
              <g className="float-2">
                <text x="400" y="60" textAnchor="middle" className="system-label" transform="rotate(5, 400, 60)">EMAIL</text>
              </g>
              
              {/* Meetings Label */}
              <g className="float-3">
                <text x="640" y="80" textAnchor="middle" className="system-label" transform="rotate(-5, 640, 80)">MEETINGS</text>
              </g>
              
              {/* Support Label */}
              <g className="float-4">
                <text x="280" y="345" textAnchor="middle" className="system-label" transform="rotate(7, 280, 345)">SUPPORT</text>
              </g>
              
              {/* Feedback Label */}
              <g className="float-5">
                <text x="520" y="350" textAnchor="middle" className="system-label" transform="rotate(-6, 520, 350)">FEEDBACK</text>
              </g>
            </g>
            
            {/* Define Gradients */}
            <defs>
              <radialGradient id="blackHoleGradient1" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#1F2937" />
                <stop offset="100%" stopColor="#111827" stopOpacity="0" />
              </radialGradient>
              
              <radialGradient id="blackHoleGradient2" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#374151" />
                <stop offset="100%" stopColor="#1F2937" stopOpacity="0.5" />
              </radialGradient>
            </defs>
          </svg>
        </div>
        
        {/* Overlaid UI Elements */}
        <div className="absolute top-2 left-2 md:left-5">
          <div 
            className="flex items-center gap-1 md:gap-2 bg-red-50 text-red-700 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm shadow-sm"
            role="alert"
          >
            <AlertCircle className="h-3 w-3 md:h-4 md:w-4" aria-hidden="true" />
            <span>Disconnected Systems</span>
          </div>
        </div>
        
        <div className="absolute bottom-2 md:bottom-4 right-2 md:right-5">
          <div 
            className="flex items-center gap-1 md:gap-2 bg-amber-50 text-amber-700 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm shadow-sm"
            role="alert"
          >
            <Clock className="h-3 w-3 md:h-4 md:w-4" aria-hidden="true" />
            <span>Data Black Hole</span>
          </div>
        </div>
      </div>
      
      {/* Pain Points */}
      <div 
        className="grid grid-cols-1 gap-4 pr-4"
        role="list"
        aria-label="Process pain points"
      >
        <div className="flex items-start gap-3 pr-4" role="listitem">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100">
            <X className="h-4 w-4 text-red-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Data Fragmentation</h3>
            <p className="text-sm text-gray-600">Information spread across multiple tools without a single source of truth</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3 pr-4" role="listitem">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100">
            <X className="h-4 w-4 text-red-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Manual Processing</h3>
            <p className="text-sm text-gray-600">Time-consuming manual triage and categorization of feedback</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3 pr-4" role="listitem">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100">
            <X className="h-4 w-4 text-red-600" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Lost Opportunities</h3>
            <p className="text-sm text-gray-600">Critical feedback gets sucked into the data black hole and forgotten</p>
          </div>
        </div>
      </div>
    </section>
  );
});

VisualComparisonLeft.displayName = 'VisualComparisonLeft';

export default VisualComparisonLeft; 